/*
PURPOSE: Editorial post-contribution confirmation page served at /thankyou.
Renders in two modes based on authentication state:
  - Authed: shows "Your coral has been created successfully!" subtitle and a
    "Return to Dashboard" CtaPill navigating to /dashboard?awaitingActivation=true.
  - Logged-out: shows "Now check your email for a magic link" subtitle; no CTA.
Both modes fire CORAL_CREATION_COMPLETED analytics with coralId from the search
params (captured for analytics only — no data fetch). A decorative HexBloom
sits behind the hero headline. Vision section composes EcosystemBloom in a 2-col
grid. Wrapped in PublicPageErrorBoundary so any render fault shows the editorial
fallback rather than sinking to NotFound.
USE CASES:
- /thankyou (authed): confirm coral creation; link back to dashboard.
- /thankyou?coralId=<id> (authed): same, analytics carries the coralId.
- /thankyou (logged-out): confirm and instruct user to check email.
DOMAIN BOUNDARIES:
- Reads copy from useCopy().publicPages.thankyou verbatim; no fabricated strings.
- coralId is for analytics only; the page does not fetch coral data.
- No imports from design-sandbox/.
*/

import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import { useAuthContext } from '../../../components/Auth/AuthContext/AuthContext';
import { useCopy } from '../../../hooks/useCopy';
import {
  EditorialSection,
  Eyebrow,
  DisplayHeading,
  BodyProse,
  CtaPill,
} from '../../../components/editorial';
import HexBloom from '../../../components/HexBloom/HexBloom';
import EcosystemBloom from '../../../components/EcosystemBloom/EcosystemBloom';
import PublicPageErrorBoundary from '../../../components/PublicPageErrorBoundary/PublicPageErrorBoundary';

const Thankyou: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuthContext();
  const copy = useCopy().publicPages.thankyou;
  // Vision is single-sourced from publicArtist.vision so the PublicArtist-subtree
  // pages cannot drift on the vision statement.
  const visionCopy = useCopy().publicPages.publicArtist.vision;

  // Fire CORAL_CREATION_COMPLETED once on mount. The dependency array includes
  // location and userProfile to match the original behaviour; in practice
  // neither changes after mount on this page.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const coralId = searchParams.get('coralId');

    trackEvent(EventName.CORAL_CREATION_COMPLETED, {
      userId: userProfile?.userId,
      coralId: coralId || undefined,
    });
  }, [location, userProfile]);

  return (
    <PublicPageErrorBoundary>
      <div className="flex flex-col min-h-screen bg-ivory">

        {/* Hero Section */}
        <EditorialSection background="ivory">
          <div className="relative">
            {/* Decorative bloom behind the headline — fixed seed, not
                data-tied; celebration intent, not per-coral identity. */}
            <div
              className="absolute top-0 right-0 pointer-events-none select-none opacity-60 -z-0"
              aria-hidden="true"
            >
              <HexBloom seed={7} size={360} />
            </div>

            <div className="relative z-10 max-w-2xl">
              <DisplayHeading as="h1" scale="display">
                {copy.hero.title}
              </DisplayHeading>

              <BodyProse className="mt-8">
                <p>{copy.hero.subtitle}</p>
              </BodyProse>

              {userProfile ? (
                <>
                  <p className="mt-4 text-editorial-body text-editorial-muted/80">
                    {copy.hero.authedSubtitle}
                  </p>
                  <div className="mt-8">
                    <CtaPill onClick={() => navigate('/dashboard?awaitingActivation=true')}>
                      {copy.cta.returnToDashboard}
                    </CtaPill>
                  </div>
                </>
              ) : (
                <p className="mt-4 text-editorial-body text-editorial-muted/80">
                  {copy.hero.loggedOutSubtitle}
                </p>
              )}
            </div>
          </div>
        </EditorialSection>

        {/* Vision Section */}
        <EditorialSection background="cream">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>{visionCopy.sectionTitle}</Eyebrow>
              <DisplayHeading as="h2" scale="heading" className="mt-8" measure={false}>
                {visionCopy.description}
              </DisplayHeading>
            </div>
            <div>
              <EcosystemBloom />
            </div>
          </div>
        </EditorialSection>

      </div>
    </PublicPageErrorBoundary>
  );
};

export default Thankyou;
